import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Shirt, Hash, ListChecks, Truck, Settings, Download, History, Users, Loader2, ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ORDER = [
  "pending_number_assignment",
  "pending_size_selection",
  "size_selected",
  "ordered",
  "fulfilled",
];

const STATUS_LABEL = {
  pending_number_assignment: "Pending #",
  pending_size_selection: "Pending size",
  size_selected: "Size selected",
  ordered: "Ordered",
  fulfilled: "Fulfilled",
};

const STATUS_COLOR = {
  pending_number_assignment: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  pending_size_selection: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  size_selected: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40",
  ordered: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  fulfilled: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

export default function AdminKitManagement() {
  const [queue, setQueue] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAge, setFilterAge] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [jerseyAge, setJerseyAge] = useState("U10");
  const [jerseyData, setJerseyData] = useState(null);
  const [jerseyAssignTarget, setJerseyAssignTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (jerseyAge) fetchJersey(jerseyAge);
  }, [jerseyAge]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/kit/admin/queue`, { withCredentials: true }),
        axios.get(`${API}/kit/admin/config`, { withCredentials: true }),
      ]);
      setQueue(q.data.rows || []);
      setConfig(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load kit data");
    } finally {
      setLoading(false);
    }
  };

  const fetchJersey = async (ag) => {
    try {
      const r = await axios.get(`${API}/kit/admin/jersey-numbers/${encodeURIComponent(ag)}`, { withCredentials: true });
      setJerseyData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load jersey grid");
    }
  };

  const filtered = useMemo(() => {
    return queue.filter((r) => {
      if (filterStatus && r.kit_status !== filterStatus) return false;
      if (filterAge && r.age_group !== filterAge) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.player_name || "").toLowerCase().includes(s) &&
            !(r.team_name || "").toLowerCase().includes(s) &&
            !(r.registration_reference || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [queue, filterStatus, filterAge, search]);

  const ageGroups = useMemo(
    () => Array.from(new Set(queue.map((r) => r.age_group).filter(Boolean))).sort(),
    [queue]
  );

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const bulkMark = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error("Select rows first"); return; }
    try {
      const r = await axios.post(`${API}/kit/admin/${action}`, { entitlement_ids: ids }, { withCredentials: true });
      toast.success(`${action.replace("mark-", "Marked ")} for ${r.data.updated} rows`);
      setSelectedIds(new Set());
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const exportCsv = () => {
    window.open(`${API}/kit/admin/order-compilation/export?fmt=csv`, "_blank");
  };
  const exportPdf = () => {
    window.open(`${API}/kit/admin/order-compilation/export?fmt=pdf`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4 text-sm" data-testid="kit-back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shirt className="w-8 h-8 text-fuchsia-400" /> Kit Ordering &amp; Management
            </h1>
            <p className="text-sm text-slate-400">Phase 1 — initial kit + replacement orders</p>
          </div>
          <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="kit-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        <Tabs defaultValue="queue" className="space-y-4">
          <TabsList className="bg-slate-900 border border-slate-700">
            <TabsTrigger value="queue" data-testid="kit-tab-queue"><ListChecks className="w-4 h-4 mr-1" /> Queue</TabsTrigger>
            <TabsTrigger value="jersey" data-testid="kit-tab-jersey"><Hash className="w-4 h-4 mr-1" /> Jersey numbers</TabsTrigger>
            <TabsTrigger value="roster" data-testid="kit-tab-roster"><Users className="w-4 h-4 mr-1" /> Number roster</TabsTrigger>
            <TabsTrigger value="compile" data-testid="kit-tab-compile"><Truck className="w-4 h-4 mr-1" /> Order compilation</TabsTrigger>
            <TabsTrigger value="config" data-testid="kit-tab-config"><Settings className="w-4 h-4 mr-1" /> Config</TabsTrigger>
          </TabsList>

          {/* QUEUE */}
          <TabsContent value="queue">
            <Card className="bg-slate-900/40 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-fuchsia-200">Kit order queue ({filtered.length} of {queue.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                  <Input placeholder="Search player / team / reference…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="kit-queue-search" />
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-slate-800 border border-slate-700 rounded px-2 text-white text-sm" data-testid="kit-queue-status-filter">
                    <option value="">All statuses</option>
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                  <select value={filterAge} onChange={(e) => setFilterAge(e.target.value)} className="bg-slate-800 border border-slate-700 rounded px-2 text-white text-sm" data-testid="kit-queue-age-filter">
                    <option value="">All age groups</option>
                    {ageGroups.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <Button size="sm" disabled={!selectedIds.size} onClick={() => bulkMark("mark-ordered")} data-testid="kit-bulk-ordered-btn">Mark ordered ({selectedIds.size})</Button>
                    <Button size="sm" variant="outline" disabled={!selectedIds.size} onClick={() => bulkMark("mark-fulfilled")} data-testid="kit-bulk-fulfilled-btn">Mark fulfilled</Button>
                  </div>
                </div>

                <div className="overflow-auto max-h-[60vh]">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-800 sticky top-0 text-slate-300">
                      <tr>
                        <th className="p-2 text-left w-8"><input type="checkbox" onChange={(e) => {
                          if (e.target.checked) setSelectedIds(new Set(filtered.map(r => r.id)));
                          else setSelectedIds(new Set());
                        }} /></th>
                        <th className="p-2 text-left">Player</th>
                        <th className="p-2 text-left">DOB</th>
                        <th className="p-2 text-left">Age group</th>
                        <th className="p-2 text-left">Team</th>
                        <th className="p-2 text-left">Reg ref</th>
                        <th className="p-2 text-left">Reg paid</th>
                        <th className="p-2 text-left">Jersey #</th>
                        <th className="p-2 text-left">Size</th>
                        <th className="p-2 text-left">Status</th>
                        <th className="p-2 text-left">Type</th>
                        <th className="p-2 text-left">Remarks</th>
                        <th className="p-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`kit-row-${r.id}`}>
                          <td className="p-2">
                            <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} data-testid={`kit-row-select-${r.id}`} />
                          </td>
                          <td className="p-2 font-medium text-white">{r.player_name}</td>
                          <td className="p-2 text-slate-400 font-mono">{r.birthdate || "—"}</td>
                          <td className="p-2">{r.age_group || <span className="text-amber-400 italic">no team</span>}</td>
                          <td className="p-2 text-slate-300">{r.team_name || "—"}</td>
                          <td className="p-2 font-mono text-slate-400">{r.registration_reference || "—"}</td>
                          <td className="p-2 text-slate-500">{r.registration_paid_at?.slice(0, 10) || "—"}</td>
                          <td className="p-2">
                            {r.jersey_number ? (
                              <span className="font-mono text-fuchsia-300">#{r.jersey_number}</span>
                            ) : (
                              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setJerseyAssignTarget(r)} disabled={!r.age_group} data-testid={`kit-assign-jersey-${r.id}`}>Assign</Button>
                            )}
                          </td>
                          <td className="p-2">{r.size_selected || "—"}</td>
                          <td className="p-2">
                            <Badge className={`text-[10px] ${STATUS_COLOR[r.kit_status] || ""}`}>{STATUS_LABEL[r.kit_status] || r.kit_status}</Badge>
                          </td>
                          <td className="p-2 text-slate-400">
                            {r.entitlement_type}
                            {r.order_reason && (
                              <span
                                className="ml-1 inline-block px-1.5 py-0 rounded text-[9px] font-medium uppercase bg-orange-500/15 text-orange-300 border border-orange-500/30"
                                title="Parent-stated reason for the order"
                                data-testid={`kit-reason-${r.id}`}
                              >
                                {r.order_reason}
                              </span>
                            )}
                          </td>
                          <td
                            className="p-2 text-xs max-w-[260px] align-top"
                            data-testid={`kit-remarks-${r.id}`}
                          >
                            <RemarksCell row={r} onSaved={fetchAll} />
                          </td>
                          <td className="p-2">
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setHistoryTarget(r)}><History className="w-3 h-3" /></Button>
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && !loading && (
                        <tr><td colSpan={13} className="p-6 text-center text-slate-500">No rows match.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* JERSEY GRID */}
          <TabsContent value="jersey">
            <Card className="bg-slate-900/40 border-slate-700 mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span>Jersey numbers in {jerseyAge}</span>
                  <select value={jerseyAge} onChange={(e) => setJerseyAge(e.target.value)} className="bg-slate-800 border border-slate-700 rounded px-2 text-white text-sm" data-testid="kit-jersey-age-select">
                    {(ageGroups.length ? ageGroups : ["U8", "U10", "U12", "U14"]).map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!jerseyData && <p className="text-slate-400">Loading…</p>}
                {jerseyData && (
                  <>
                    <p className="text-xs text-slate-400 mb-2">{jerseyData.assigned_count} assigned</p>
                    <div className="grid grid-cols-10 gap-1 mb-4">
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => {
                        const owner = jerseyData.assigned.find((a) => a.jersey_number === n);
                        return (
                          <div key={n} className={`p-2 rounded text-center ${owner ? "bg-fuchsia-500/20 border border-fuchsia-500/40" : "bg-slate-800/40 border border-slate-700"}`} data-testid={`jersey-cell-${n}`}>
                            <p className="text-sm font-bold">{n}</p>
                            {owner && <p className="text-[9px] text-slate-300 truncate" title={owner.player_name}>{owner.player_name}</p>}
                            {!owner && <p className="text-[9px] text-slate-500">—</p>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            <PlayerJerseyEditor onSaved={() => fetchJersey(jerseyAge)} />
            <BulkJerseyCsvPanel onCommitted={() => fetchJersey(jerseyAge)} />
          </TabsContent>

          {/* NUMBER ROSTER */}
          <TabsContent value="roster">
            <NumberRosterPanel />
          </TabsContent>

          {/* COMPILE */}
          <TabsContent value="compile">
            <OrderCompilationView onExportCsv={exportCsv} onExportPdf={exportPdf} />
          </TabsContent>

          {/* CONFIG */}
          <TabsContent value="config">
            <ConfigPanel config={config} onSave={(c) => setConfig(c)} />
            <StockPanel />
            <NudgePanel />
            <Phase2BackfillPanel />
          </TabsContent>
        </Tabs>
      </div>

      {jerseyAssignTarget && (
        <AssignJerseyDialog
          target={jerseyAssignTarget}
          onClose={() => setJerseyAssignTarget(null)}
          onAssigned={() => { setJerseyAssignTarget(null); fetchAll(); fetchJersey(jerseyAge); }}
        />
      )}
      {historyTarget && (
        <PlayerHistoryDialog target={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
    </div>
  );
}

function RemarksCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [reply, setReply] = useState(row.admin_remark_reply || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/kit/admin/set-remark-reply`, {
        entitlement_id: row.id,
        admin_remark: reply.trim() || null,
      }, { withCredentials: true });
      toast.success(reply.trim() ? "Reply saved" : "Reply cleared");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  if (editing) {
    return (
      <div className="space-y-1">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value.slice(0, 300))}
          rows={2}
          className="w-full bg-slate-800 border border-slate-700 rounded p-1 text-[11px] text-cyan-100"
          placeholder="Reply to parent — e.g., 'Confirmed #7 recorded'"
          data-testid={`kit-admin-reply-input-${row.id}`}
        />
        <div className="flex items-center gap-1">
          <Button size="sm" className="h-6 text-[10px] px-2 bg-cyan-600 hover:bg-cyan-500" disabled={busy} onClick={save} data-testid={`kit-admin-reply-save-${row.id}`}>
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => { setEditing(false); setReply(row.admin_remark_reply || ""); }} data-testid={`kit-admin-reply-cancel-${row.id}`}>
            Cancel
          </Button>
          <span className="text-[9px] text-slate-500 ml-auto">{reply.length}/300</span>
        </div>
      </div>
    );
  }
  const hasParentMsg = !!row.parent_remarks;
  const hasAdminReply = !!row.admin_remark_reply;
  if (!hasParentMsg && !hasAdminReply) {
    return (
      <button
        type="button"
        className="text-[10px] text-slate-500 hover:text-cyan-300 underline decoration-dashed"
        onClick={() => setEditing(true)}
        data-testid={`kit-admin-reply-add-${row.id}`}
      >
        + note
      </button>
    );
  }
  return (
    <div className="space-y-1">
      {hasParentMsg && (
        <div
          className="inline-block px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 whitespace-pre-wrap break-words text-[11px]"
          title={row.parent_remarks}
        >
          <span className="opacity-60 mr-1">Parent:</span>{row.parent_remarks}
        </div>
      )}
      {hasAdminReply && (
        <div
          className="inline-block px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 whitespace-pre-wrap break-words text-[11px]"
          title={row.admin_remark_reply}
        >
          <span className="opacity-60 mr-1">Admin:</span>{row.admin_remark_reply}
        </div>
      )}
      <button
        type="button"
        className="block text-[10px] text-slate-500 hover:text-cyan-300 underline decoration-dashed"
        onClick={() => setEditing(true)}
        data-testid={`kit-admin-reply-edit-${row.id}`}
      >
        {hasAdminReply ? "edit reply" : "+ reply"}
      </button>
    </div>
  );
}

function AssignJerseyDialog({ target, onClose, onAssigned }) {
  const [num, setNum] = useState("");
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!num || !target.age_group) return;
    setBusy(true);
    try {
      await axios.post(`${API}/kit/admin/assign-jersey`, {
        player_id: target.player_id,
        age_group: target.age_group,
        jersey_number: Number(num),
        override_existing: override,
        override_reason: reason,
      }, { withCredentials: true });
      toast.success(`Assigned #${num} to ${target.player_name}`);
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 text-white border-slate-700">
        <DialogHeader>
          <DialogTitle>Assign jersey number — {target.player_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-400">Age group: <strong className="text-white">{target.age_group || "(no team)"}</strong></p>
          <div>
            <Label className="text-xs">Jersey number</Label>
            <Input type="number" min={1} value={num} onChange={(e) => setNum(e.target.value)} data-testid="kit-jersey-num-input" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
            Override existing assignment for this player (logs audit entry)
          </label>
          {override && (
            <Input placeholder="Override reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="kit-override-reason-input" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !num || (override && !reason)} data-testid="kit-jersey-confirm-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlayerHistoryDialog({ target, onClose }) {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    axios.get(`${API}/kit/admin/player-history/${target.player_id}`, { withCredentials: true })
      .then((r) => setHist(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  }, [target.player_id]);
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 text-white border-slate-700 max-w-3xl">
        <DialogHeader>
          <DialogTitle>Kit history — {target.player_name}</DialogTitle>
        </DialogHeader>
        {!hist && <p className="text-slate-400">Loading…</p>}
        {hist && (
          <div className="text-xs space-y-2">
            {hist.jersey_assignment && (
              <p className="text-fuchsia-300">Jersey: #{hist.jersey_assignment.jersey_number} ({hist.jersey_assignment.age_group}) — assigned {hist.jersey_assignment.assigned_at?.slice(0, 10)} by {hist.jersey_assignment.assigned_by}</p>
            )}
            {(hist.entitlements || []).map((e) => (
              <div key={e.id} className="bg-slate-800/40 p-2 rounded">
                <p><strong>{e.entitlement_type}</strong> · {STATUS_LABEL[e.kit_status] || e.kit_status} · size: {e.size_selected || "—"} · created {e.created_at?.slice(0, 10)}</p>
                {(e.items_ordered || []).length > 0 && (
                  <p className="text-slate-400">items: {e.items_ordered.map((i) => `${i.item_type}@${i.size || "?"}`).join(" · ")}</p>
                )}
                {(e.admin_override_log || []).length > 0 && (
                  <details className="text-amber-300 mt-1">
                    <summary>Override log ({e.admin_override_log.length})</summary>
                    <pre className="text-[10px]">{JSON.stringify(e.admin_override_log, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrderCompilationView({ onExportCsv, onExportPdf }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.post(`${API}/kit/admin/order-compilation`, {}, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  }, []);
  return (
    <Card className="bg-slate-900/40 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span>Order compilation — {data?.total_rows_to_order ?? "…"} player(s) ready</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onExportCsv} data-testid="kit-export-csv"><Download className="w-3 h-3 mr-1" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={onExportPdf} data-testid="kit-export-pdf"><Download className="w-3 h-3 mr-1" /> PDF</Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data && <p className="text-slate-400">Loading…</p>}
        {data && Object.keys(data.by_age_group).length === 0 && <p className="text-slate-400 py-6 text-center">Nothing to order — no rows in size_selected status.</p>}
        {data && Object.keys(data.by_age_group).sort().map((ag) => (
          <div key={ag} className="mb-4 bg-slate-800/40 p-3 rounded">
            <h3 className="text-fuchsia-300 font-medium mb-2">{ag}</h3>
            <p className="text-xs text-slate-400 mb-1">Sizes:</p>
            <ul className="text-sm grid grid-cols-2 md:grid-cols-3 gap-1 mb-2">
              {Object.entries(data.by_age_group[ag].by_size).sort().map(([sz, n]) => (
                <li key={sz} className="font-mono">{n}× <strong>{sz}</strong></li>
              ))}
            </ul>
            <details>
              <summary className="text-xs cursor-pointer text-violet-300">Players ({data.by_age_group[ag].rows.length})</summary>
              <ul className="text-[11px] mt-1 space-y-0.5">
                {data.by_age_group[ag].rows.map((p) => (
                  <li key={p.entitlement_id}>#{p.jersey_number || "?"} · {p.player_name} · {p.size_selected} ({p.entitlement_type})</li>
                ))}
              </ul>
            </details>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ConfigPanel({ config, onSave }) {
  const [draft, setDraft] = useState(config);
  useEffect(() => { setDraft(config); }, [config]);
  if (!draft) return <p className="text-slate-400">Loading…</p>;

  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updatePricing = (k, v) => setDraft((d) => ({ ...d, pricing: { ...d.pricing, [k]: Number(v) || 0 } }));

  const save = async () => {
    try {
      const r = await axios.put(`${API}/kit/admin/config`, draft, { withCredentials: true });
      onSave(r.data);
      toast.success("Config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle>Kit configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <Label className="text-xs">Available sizes (comma-separated)</Label>
          <Input
            value={(draft.available_sizes || []).join(", ")}
            onChange={(e) => update("available_sizes", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            data-testid="kit-config-sizes"
          />
        </div>
        <div>
          <Label className="text-xs">Size chart image URL</Label>
          <Input
            value={draft.size_chart_image_url || ""}
            onChange={(e) => update("size_chart_image_url", e.target.value)}
            data-testid="kit-config-chart-url"
          />
          {draft.size_chart_image_url && (
            <img src={draft.size_chart_image_url} alt="Size chart preview" className="mt-2 max-w-md rounded border border-slate-700" />
          )}
        </div>
        <div className="grid grid-cols-4 gap-2">
          {["full_set", "jersey", "shorts", "socks"].map((k) => (
            <div key={k}>
              <Label className="text-xs capitalize">{k.replace("_", " ")} ($)</Label>
              <Input type="number" value={draft.pricing?.[k] ?? 0} onChange={(e) => updatePricing(k, e.target.value)} data-testid={`kit-config-price-${k}`} />
            </div>
          ))}
        </div>
        <div>
          <Label className="text-xs">Disclaimer text (parent must accept)</Label>
          <textarea
            value={draft.disclaimer_text || ""}
            onChange={(e) => update("disclaimer_text", e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-sm min-h-[80px]"
            data-testid="kit-config-disclaimer"
          />
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!draft.registration_fee_kit_included} onChange={(e) => update("registration_fee_kit_included", e.target.checked)} />
          Initial kit included in registration fee
        </label>
        <Button onClick={save} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="kit-config-save-btn">Save config</Button>
      </CardContent>
    </Card>
  );
}

function NudgePanel() {
  const [days, setDays] = useState(7);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/kit/admin/nudge-eligible?days=${days}`, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    if (!preview?.rows?.length) { toast.error("Nothing to send — refresh preview first"); return; }
    if (!window.confirm(`Send size-selection reminder emails to ${preview.rows.length} parent(s)?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/kit/admin/send-nudges`, { days }, { withCredentials: true });
      const { sent = 0, attempted = 0, failures = [] } = r.data;
      toast.success(`Sent ${sent}/${attempted}. ${failures.length} failure(s).`);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Card className="mt-4 bg-slate-800/40 border-amber-500/30" data-testid="nudge-panel">
      <CardHeader>
        <CardTitle className="text-amber-300 text-base flex items-center gap-2">
          🔔 Size-selection reminders
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Send parents a friendly nudge if they haven&apos;t picked a size for their child&apos;s kit within N days. Auto-skips anyone already nudged in the last 3 days.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-300">
            Threshold
            <input
              type="number"
              min={1}
              max={60}
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 7)}
              className="ml-2 w-16 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
              data-testid="nudge-threshold-input"
            />
            <span className="ml-1 text-slate-500">day(s) since entitlement created</span>
          </label>
          <Button size="sm" variant="outline" onClick={loadPreview} disabled={busy} data-testid="nudge-preview-btn">
            {busy ? "Loading…" : "Preview eligible"}
          </Button>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-500"
            onClick={sendAll}
            disabled={sending || !preview?.rows?.length}
            data-testid="nudge-send-all-btn"
          >
            {sending ? "Sending…" : `Send ${preview?.rows?.length || 0} nudge(s)`}
          </Button>
        </div>
        {preview && (
          <div className="border border-slate-700 rounded max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/60 text-slate-400 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Parent</th>
                  <th className="p-2 text-left">Child</th>
                  <th className="p-2 text-left">Created</th>
                  <th className="p-2 text-left">Last nudged</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 ? (
                  <tr><td colSpan={4} className="p-3 text-center text-slate-500">No eligible parents right now.</td></tr>
                ) : (
                  preview.rows.map((r) => (
                    <tr key={r.entitlement_id} className="border-t border-slate-800">
                      <td className="p-2">{r.parent_email || "—"}</td>
                      <td className="p-2 text-slate-300">{r.child_name}</td>
                      <td className="p-2 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                      <td className="p-2 text-slate-500">{r.last_size_nudge_at ? r.last_size_nudge_at.slice(0, 10) : "never"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Phase2BackfillPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Set of player_ids currently TICKED (default = every candidate from the
  // last dry-run). Admin unchecks the ones they want to exclude. Only
  // checked ids are sent to the execute call.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const run = async (dryRun) => {
    const checkedCount = selectedIds.size;
    if (!dryRun) {
      if (checkedCount === 0) {
        toast.error("No players selected — tick at least one before executing");
        return;
      }
      if (!window.confirm(
        `Execute Phase 2 kit backfill for ${checkedCount} selected player${checkedCount === 1 ? "" : "s"}?\n\n` +
        "Only the ticked players will have entitlements created. Unticked players are skipped."
      )) return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ dry_run: dryRun ? "true" : "false" });
      if (!dryRun) params.set("confirm_token", "RUN_KIT_PHASE2_BACKFILL_APR2026");
      const body = dryRun ? {} : { player_ids: Array.from(selectedIds) };
      const r = await axios.post(
        `${API}/kit/admin/phase2-backfill?${params.toString()}`,
        body,
        { withCredentials: true },
      );
      setResult(r.data);
      if (dryRun) {
        // Default-tick every candidate the dry-run surfaced
        const ids = new Set((r.data.sample_players || []).map((p) => p.player_id));
        setSelectedIds(ids);
        toast.success(`Dry-run: ${r.data.would_create} candidate${r.data.would_create === 1 ? "" : "s"} found`);
      } else {
        toast.success(`Created ${r.data.created} entitlement${r.data.created === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
      setResult({ error: err.response?.data?.detail || String(err) });
    } finally {
      setLoading(false);
    }
  };

  const togglePlayer = (pid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (!result?.sample_players) return;
    setSelectedIds(checked ? new Set(result.sample_players.map((p) => p.player_id)) : new Set());
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700 mt-4" data-testid="kit-phase2-panel">
      <CardHeader>
        <CardTitle className="text-fuchsia-200 flex items-center gap-2">
          <Users className="w-5 h-5" /> Phase 2 — Retroactive backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-slate-300">
        <p>
          Scans every paid <code className="bg-slate-800 px-1 rounded">academy_registration</code> transaction directly and surfaces every paid child not yet in the kit system. Tick the players you want to backfill in the preview list, then click <strong>Execute</strong>. Only ticked players get entitlements created.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => run(true)}
            disabled={loading}
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            data-testid="kit-phase2-dryrun-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview (dry-run)"}
          </Button>
          <Button
            onClick={() => run(false)}
            disabled={loading || !result || result.dry_run !== true || selectedIds.size === 0}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-40"
            data-testid="kit-phase2-execute-btn"
            title={
              !result?.dry_run ? "Run a dry-run first"
              : selectedIds.size === 0 ? "Tick at least one player"
              : `Execute for ${selectedIds.size} selected player${selectedIds.size === 1 ? "" : "s"}`
            }
          >
            Execute for {selectedIds.size} selected
          </Button>
        </div>

        {result && !result.error && (
          <div className={`p-3 rounded border ${result.dry_run ? "bg-amber-500/10 border-amber-500/40" : "bg-emerald-500/10 border-emerald-500/40"}`}>
            {result.dry_run ? (
              <>
                <p className="text-amber-200 font-medium" data-testid="kit-phase2-dryrun-summary">
                  Dry-run: {result.would_create} candidate{result.would_create === 1 ? "" : "s"} found · {selectedIds.size} ticked
                </p>
                <p className="text-xs text-amber-300/80 mt-1">
                  Scanned {result.scanned_txns ?? 0} paid academy_registration txns · {result.total_candidates} unique candidate player{result.total_candidates === 1 ? "" : "s"}
                </p>
                <div className="mt-2 text-[11px] text-amber-200/80 bg-amber-500/10 rounded p-2">
                  <p className="font-medium uppercase tracking-wide">Candidates breakdown</p>
                  <ul className="mt-1 space-y-0.5">
                    <li>Already have entitlement (skipped): <span className="font-mono">{result.already_have_entitlement}</span></li>
                    <li>Kit already issued this term (skipped): <span className="font-mono">{result.skipped_kit_issued_for_term}</span></li>
                    <li>Eligible candidates: <span className="font-mono text-amber-200">{result.would_create}</span></li>
                  </ul>
                </div>

                {result.unresolved_player_ids && result.unresolved_player_ids.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-amber-400 cursor-pointer">
                      ⚠ {result.unresolved_player_ids.length} unresolved player_id{result.unresolved_player_ids.length === 1 ? "" : "s"} (referenced in txns but no academy_players row)
                    </summary>
                    <ul className="mt-1 text-[10px] text-slate-400 space-y-0.5 font-mono">
                      {result.unresolved_player_ids.map((pid) => <li key={pid}>{pid}</li>)}
                    </ul>
                  </details>
                )}

                {result.skipped_kit_issued_sample && result.skipped_kit_issued_sample.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-slate-300 cursor-pointer">
                      🛡 {result.skipped_kit_issued_for_term} player{result.skipped_kit_issued_for_term === 1 ? "" : "s"} skipped (kit already issued this term)
                    </summary>
                    <ul className="mt-1 text-[11px] text-slate-300 space-y-1 max-h-[200px] overflow-y-auto pr-1">
                      {result.skipped_kit_issued_sample.map((s) => (
                        <li key={s.player_id} className="bg-slate-900/40 border border-slate-700/40 rounded p-1.5">
                          <span className="font-medium text-white">{s.player_name}</span>
                          <span className="text-[10px] text-slate-400 ml-2">term {s.term_id || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {result.sample_players && result.sample_players.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-amber-200 mb-2">
                      <span>
                        Eligible candidates ({result.sample_players.length}{result.full_list_count > result.sample_players.length ? ` of ${result.full_list_count}` : ""}) — tick to include
                      </span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => toggleAll(true)}
                          className="text-[10px] text-amber-300 hover:text-amber-100 underline"
                          data-testid="kit-phase2-tick-all"
                        >
                          Tick all
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAll(false)}
                          className="text-[10px] text-amber-300 hover:text-amber-100 underline"
                          data-testid="kit-phase2-untick-all"
                        >
                          Untick all
                        </button>
                      </div>
                    </div>
                    <ul className="text-[11px] text-slate-200 space-y-1.5 max-h-[600px] overflow-y-auto pr-1" data-testid="kit-phase2-candidate-list">
                      {result.sample_players.map((p) => {
                        const checked = selectedIds.has(p.player_id);
                        return (
                          <li
                            key={p.player_id}
                            className={`bg-slate-900/40 border rounded p-2 transition-colors ${checked ? "border-emerald-500/40" : "border-slate-700/40 opacity-60"}`}
                          >
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePlayer(p.player_id)}
                                className="mt-0.5 cursor-pointer accent-emerald-500"
                                data-testid={`kit-phase2-candidate-cb-${p.player_id}`}
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between flex-wrap gap-1">
                                  <span className="font-medium text-white">{p.player_name || "(no name)"}</span>
                                  <span className="text-[10px] text-amber-300/80">
                                    term {p.term_id || "—"}{p.team_id ? ` · team ${p.team_id}` : ""}
                                  </span>
                                </div>
                                {p.evidence && p.evidence.length > 0 && (
                                  <ul className="mt-1 ml-1 text-[10px] text-emerald-300/80 space-y-0.5">
                                    {p.evidence.map((ev, i) => (
                                      <li key={i}>
                                        ✓ reg_fee ${Number(ev.registration_fee).toFixed(2)} on txn <code>{ev.txn_id?.slice(0, 12)}…</code>
                                        {ev.payment_reference ? ` · ${ev.payment_reference}` : ""}
                                        {ev.child_name ? ` (per-child: ${ev.child_name})` : ""}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-emerald-200 font-medium" data-testid="kit-phase2-execute-summary">
                  ✓ Created {result.created} entitlement{result.created === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-emerald-300/80 mt-1">
                  Already had entitlement: {result.already_have_entitlement} · Kit already issued this term: {result.skipped_kit_issued_for_term} · Errors: {result.error_count}
                </p>
                {result.unknown_selected_ids && result.unknown_selected_ids.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-amber-300 cursor-pointer">
                      ⚠ {result.unknown_selected_ids.length} selected id{result.unknown_selected_ids.length === 1 ? "" : "s"} were not eligible (skipped)
                    </summary>
                    <ul className="mt-1 text-[10px] text-slate-400 font-mono space-y-0.5">
                      {result.unknown_selected_ids.map((pid) => <li key={pid}>{pid}</li>)}
                    </ul>
                  </details>
                )}
              </>
            )}
          </div>
        )}

        {result?.error && (
          <div className="p-3 rounded border bg-red-500/10 border-red-500/40 text-red-300 text-xs">{result.error}</div>
        )}
      </CardContent>
    </Card>
  );
}



function StockPanel() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kit/stock`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load stock");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    const key = `${r.item_type}|${r.size}`;
    const q = parseInt(draft[key], 10);
    if (Number.isNaN(q) || q < 0) { toast.error("Enter a quantity of 0 or more"); return; }
    setBusy(key);
    try {
      await axios.post(`${API}/kit/admin/stock`, {
        item_type: r.item_type, size: r.size, qty_in: q,
      }, { withCredentials: true });
      toast.success(`${r.item_type} ${r.size}: ${q} in stock`);
      setDraft((d) => ({ ...d, [key]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy("");
    }
  };

  const byType = {};
  rows.forEach((r) => { (byType[r.item_type] = byType[r.item_type] || []).push(r); });

  return (
    <Card className="bg-slate-900/40 border-slate-700 mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-fuchsia-200 text-base">Kit stock by size</CardTitle>
        <p className="text-xs text-slate-400">
          Enter how many you have taken in. Coaches see what's left on the handover screen, so they
          know which size they can swap into.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-slate-400">Loading…</p>}
        {Object.keys(byType).map((itype) => (
          <div key={itype} data-testid={`stock-group-${itype}`}>
            <h4 className="text-sm font-semibold text-white capitalize mb-2">{itype}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {byType[itype].map((r) => {
                const key = `${r.item_type}|${r.size}`;
                return (
                  <div key={key} className="flex items-center gap-2 bg-slate-800/40 rounded px-2 py-2" data-testid={`stock-row-${itype}-${r.size}`}>
                    <span className="text-xs text-slate-300 w-16 shrink-0">{r.size}</span>
                    <span className={`text-xs font-mono w-20 shrink-0 ${r.available <= 0 ? "text-red-300" : r.available <= 2 ? "text-amber-300" : "text-emerald-300"}`}>
                      {r.available} left
                    </span>
                    <input
                      type="number" min="0"
                      placeholder={String(r.qty_in)}
                      value={draft[key] ?? ""}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-1 text-xs font-mono"
                      data-testid={`stock-input-${itype}-${r.size}`}
                    />
                    <Button size="sm" variant="outline" className="h-7 text-[10px]"
                      disabled={busy === key}
                      onClick={() => save(r)}
                      data-testid={`stock-save-${itype}-${r.size}`}>Set</Button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}


function NumberRosterPanel() {
  const [data, setData] = useState(null);
  const [ag, setAg] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");

  const load = async (group = ag) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kit/admin/number-roster`, {
        params: group ? { age_group: group } : {}, withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load number roster");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(ag); /* eslint-disable-next-line */ }, [ag]);

  const assign = async (p, ageGroup) => {
    const n = parseInt(edit[p.player_id], 10);
    if (!n || n <= 0) { toast.error("Enter a number above 0"); return; }
    setBusy(p.player_id);
    try {
      await axios.post(`${API}/kit/admin/assign-jersey`, {
        player_id: p.player_id,
        age_group: ageGroup,
        jersey_number: n,
        override_existing: p.jersey_number != null,
        override_reason: "changed from admin number roster",
      }, { withCredentials: true });
      toast.success(`${p.player_name} is now #${n}`);
      setEdit((s) => ({ ...s, [p.player_id]: "" }));
      load(ag);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    } finally {
      setBusy("");
    }
  };

  const autofill = async (t) => {
    const missing = (t.players || []).filter((p) => p.jersey_number == null).length;
    if (!window.confirm(`Give the next free numbers to ${missing} player(s) in ${t.team_name}?`)) return;
    setBusy(t.team_id);
    try {
      const r = await axios.post(`${API}/kit/coach/autofill-numbers`, { team_id: t.team_id }, { withCredentials: true });
      toast.success(r.data.message || `Numbered ${r.data.assigned_count} player(s)`);
      load(ag);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fill failed");
    } finally {
      setBusy("");
    }
  };

  const release = async (p, ageGroup) => {    if (!window.confirm(`Free #${p.jersey_number} from ${p.player_name}? Anyone in ${ageGroup || "this age group"} can then take it.`)) return;
    setBusy(p.player_id);
    try {
      await axios.post(`${API}/kit/admin/release-jersey`, { player_id: p.player_id }, { withCredentials: true });
      toast.success(`Freed #${p.jersey_number}`);
      load(ag);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release");
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-fuchsia-200">Kit number roster</span>
          <div className="flex items-center gap-2">
            <select
              value={ag}
              onChange={(e) => setAg(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
              data-testid="roster-age-select"
            >
              <option value="">All age groups</option>
              {(data?.age_groups || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={() => load(ag)} data-testid="roster-refresh">Refresh</Button>
          </div>
        </CardTitle>
        {data?.totals && (
          <p className="text-xs text-slate-400" data-testid="roster-totals">
            {data.totals.assigned} assigned · {data.totals.unassigned} without a number
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && <p className="text-slate-400">Loading…</p>}
        {!loading && (data?.teams || []).length === 0 && (
          <p className="text-slate-400" data-testid="roster-empty">No squads found.</p>
        )}
        {(data?.teams || []).map((t) => (
          <div key={t.team_id} data-testid={`roster-team-${t.team_id}`}>
            <div className="flex flex-wrap items-baseline gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">{t.team_name}</h3>
              <span className="text-xs text-slate-400">{t.age_group || "—"}</span>
              {t.free_numbers?.length > 0 && (
                <span className="text-[11px] text-slate-500">free: {t.free_numbers.slice(0, 10).join(", ")}</span>
              )}
              {(t.players || []).some((p) => p.jersey_number == null) && (
                <Button size="sm" variant="outline" className="h-7 text-[10px] border-emerald-500/40 text-emerald-200 ml-auto"
                  onClick={() => autofill(t)}
                  disabled={busy === t.team_id}
                  data-testid={`roster-autofill-${t.team_id}`}>
                  {busy === t.team_id ? "Numbering…" : "Auto-number missing"}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[640px]">
                <thead className="text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="p-2 text-left">Player</th>
                    <th className="p-2 text-center">Number</th>
                    <th className="p-2 text-center">Size ordered</th>
                    <th className="p-2 text-center">Size given</th>
                    <th className="p-2 text-left">Set / change</th>
                  </tr>
                </thead>
                <tbody>
                  {(t.players || []).map((p) => {
                    const lastSwap = (p.size_swaps || [])[(p.size_swaps || []).length - 1];
                    return (
                      <tr key={p.player_id} className="border-b border-slate-800/40" data-testid={`roster-row-${p.player_id}`}>
                        <td className="p-2 text-white">{p.player_name}</td>
                        <td className="p-2 text-center font-mono text-fuchsia-300">
                          {p.jersey_number ?? <span className="text-slate-600">—</span>}
                        </td>
                        <td className="p-2 text-center text-slate-300">{p.size_ordered || "—"}</td>
                        <td className="p-2 text-center">
                          {p.sizes_issued?.length ? (
                            <span className={lastSwap ? "text-amber-300" : "text-slate-300"} title={lastSwap ? `Ordered ${lastSwap.ordered_size}, given ${lastSwap.issued_size}${lastSwap.reason ? ` — ${lastSwap.reason}` : ""}` : ""}>
                              {p.sizes_issued.join(", ")}{lastSwap ? " ⚠" : ""}
                            </span>
                          ) : <span className="text-slate-600">—</span>}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="number" min="1"
                              value={edit[p.player_id] ?? ""}
                              onChange={(e) => setEdit((s) => ({ ...s, [p.player_id]: e.target.value }))}
                              className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-1 font-mono"
                              placeholder="#"
                              data-testid={`roster-input-${p.player_id}`}
                            />
                            <Button size="sm" variant="outline" className="h-7 text-[10px]"
                              disabled={busy === p.player_id}
                              onClick={() => assign(p, t.age_group)}
                              data-testid={`roster-save-${p.player_id}`}>Save</Button>
                            {p.jersey_number != null && (
                              <Button size="sm" variant="outline" className="h-7 text-[10px] border-red-500/40 text-red-300"
                                disabled={busy === p.player_id}
                                onClick={() => release(p, t.age_group)}
                                data-testid={`roster-release-${p.player_id}`}>Free</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}


function PlayerJerseyEditor({ onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios.get(`${API}/kit/admin/config`, { withCredentials: true })
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  const search = async () => {
    setSearching(true);
    try {
      const r = await axios.get(`${API}/kit/admin/players-search`, {
        params: { q: q.trim() }, withCredentials: true,
      });
      setResults(r.data?.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.jersey_number_input) {
        const jn = parseInt(editing.jersey_number_input, 10);
        if (Number.isNaN(jn) || jn <= 0) { toast.error("Jersey number must be a positive integer"); setSaving(false); return; }
        if (!editing.age_group) { toast.error("Player has no team age_group"); setSaving(false); return; }
        await axios.post(`${API}/kit/admin/assign-jersey`, {
          player_id: editing.id,
          age_group: editing.age_group,
          jersey_number: jn,
          override_existing: !!editing.jersey_number,
          override_reason: "manual edit via admin Jersey Numbers tab",
        }, { withCredentials: true });
      }
      if (editing.size_input) {
        await axios.post(`${API}/kit/admin/set-jersey-size`, {
          player_id: editing.id,
          size: editing.size_input,
        }, { withCredentials: true });
      }
      toast.success(`Saved ${editing.name}`);
      setEditing(null);
      onSaved && onSaved();
      search();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700 mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-fuchsia-200 text-base">Manual edit — find a player</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
            placeholder="Search by player name…"
            className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm"
            data-testid="kit-player-search-input"
          />
          <Button onClick={search} disabled={searching} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="kit-player-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </Button>
        </div>

        {results.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400 border-b border-slate-700">
              <tr>
                <th className="p-2 text-left">Player</th>
                <th className="p-2 text-left">Team / Age</th>
                <th className="p-2 text-center">Jersey #</th>
                <th className="p-2 text-center">Size</th>
                <th className="p-2 text-center">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {results.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/40 hover:bg-slate-800/30" data-testid={`kit-player-row-${p.id}`}>
                  <td className="p-2 text-white">{p.name}</td>
                  <td className="p-2 text-slate-400 text-xs">{p.team_name || "—"} · {p.age_group || "?"}</td>
                  <td className="p-2 text-center font-mono">{p.jersey_number ?? <span className="text-slate-600">—</span>}</td>
                  <td className="p-2 text-center font-mono">{p.size_selected ?? <span className="text-slate-600">—</span>}</td>
                  <td className="p-2 text-center text-[10px] text-slate-400">{p.kit_status || "—"}</td>
                  <td className="p-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px]"
                      onClick={() => setEditing({
                        ...p,
                        jersey_number_input: p.jersey_number || "",
                        size_input: p.size_selected || "",
                      })}
                      data-testid={`kit-edit-player-${p.id}`}
                    >Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editing && (
          <Dialog open={true} onOpenChange={() => setEditing(null)}>
            <DialogContent className="bg-slate-900 text-white border-slate-700 max-w-md">
              <DialogHeader>
                <DialogTitle>Edit kit details — {editing.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-slate-400">Team {editing.team_name || "—"} · Age {editing.age_group || "?"}</p>
                <label className="block text-sm">
                  Jersey number
                  <input
                    type="number" min="1"
                    value={editing.jersey_number_input}
                    onChange={(e) => setEditing((s) => ({ ...s, jersey_number_input: e.target.value }))}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2 font-mono"
                    data-testid="kit-edit-jersey-input"
                  />
                  {editing.jersey_number && (
                    <span className="text-[10px] text-amber-300">Currently #{editing.jersey_number} — saving will override.</span>
                  )}
                </label>
                <label className="block text-sm">
                  Jersey size
                  <select
                    value={editing.size_input || ""}
                    onChange={(e) => setEditing((s) => ({ ...s, size_input: e.target.value }))}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2"
                    data-testid="kit-edit-size-select"
                  >
                    <option value="">— Choose a size —</option>
                    {(config?.available_sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="kit-edit-save-btn">
                  {saving ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}

function BulkJerseyCsvPanel({ onCommitted }) {
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [overrideConflicts, setOverrideConflicts] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/kit/admin/jersey-bulk/template`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kit-jersey-template-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Template download failed");
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const r = await axios.post(`${API}/kit/admin/jersey-bulk/preview`,
        { csv_text: csvText }, { withCredentials: true });
      setPreview(r.data);
      toast.success(
        `Preview: ${r.data.matched_count} matched · ${r.data.unmatched_count} unmatched · ${r.data.conflict_count} conflict${r.data.conflict_count === 1 ? "" : "s"}`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Commit jersey numbers + sizes for ${preview.matched_count} matched player(s)?\n\n` +
      (preview.unmatched_count > 0 ? `${preview.unmatched_count} unmatched row(s) will be skipped (no records created).\n\n` : "") +
      (preview.conflict_count > 0
        ? overrideConflicts
          ? `${preview.conflict_count} conflict(s) will be RESOLVED by overriding the previous holder.\n\n`
          : `${preview.conflict_count} conflict(s) will be skipped — toggle "Override conflicts" to resolve them.\n\n`
        : "")
    )) return;
    setCommitting(true);
    try {
      const r = await axios.post(`${API}/kit/admin/jersey-bulk/commit`,
        { csv_text: csvText, override_conflicts: overrideConflicts },
        { withCredentials: true });
      const ok = (r.data?.outcomes || []).filter(
        (o) => o.jersey_number_status?.startsWith("assigned") || o.jersey_size_status === "set",
      ).length;
      toast.success(`Committed — ${ok} row(s) updated`);
      setPreview(r.data);
      onCommitted && onCommitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-cyan-200 text-base flex items-center gap-2">
          <Download className="w-4 h-4" /> Bulk CSV upload
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-2">
          <Button onClick={downloadTemplate} variant="outline" className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10" data-testid="kit-bulk-template-btn">
            <Download className="w-4 h-4 mr-1" /> Download CSV template
          </Button>
          <input
            type="file" accept=".csv,text/csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="kit-bulk-file-input"
            className="text-xs file:bg-slate-800 file:border file:border-slate-600 file:rounded file:px-3 file:py-1 file:text-cyan-200 file:hover:bg-slate-700"
          />
        </div>
        <p className="text-xs text-slate-400">
          Required columns: <code>player_name, team, jersey_size, jersey_number</code>. The template prefills all enrolled players + already-assigned values. Unmatched rows are flagged for review and will NOT create new records.
        </p>
        <textarea
          rows={8}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder="Paste CSV here, or upload a file above."
          className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-xs font-mono"
          data-testid="kit-bulk-csv-textarea"
        />
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={runPreview} disabled={!csvText.trim() || loading} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="kit-bulk-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
          </Button>
          <Button onClick={commit} disabled={!preview || preview.matched_count === 0 || committing} className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40" data-testid="kit-bulk-commit-btn">
            {committing ? "Committing…" : "Commit changes"}
          </Button>
          <label className="text-[11px] text-amber-200 flex items-center gap-1 ml-2">
            <input type="checkbox" checked={overrideConflicts} onChange={(e) => setOverrideConflicts(e.target.checked)} data-testid="kit-bulk-override-conflicts" />
            Override conflicts
          </label>
        </div>

        {preview && (
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 bg-emerald-500/10 border border-emerald-500/40 rounded">
                <p className="font-medium text-emerald-200">Matched: {preview.matched_count}</p>
              </div>
              <div className="p-2 bg-red-500/10 border border-red-500/40 rounded">
                <p className="font-medium text-red-200">Unmatched: {preview.unmatched_count}</p>
              </div>
              <div className="p-2 bg-amber-500/10 border border-amber-500/40 rounded">
                <p className="font-medium text-amber-200">Conflicts: {preview.conflict_count || 0}</p>
              </div>
            </div>
            {preview.unmatched_count > 0 && (
              <details>
                <summary className="text-xs text-red-300 cursor-pointer">Unmatched rows ({preview.unmatched_count}) — will be skipped</summary>
                <ul className="mt-1 space-y-0.5 text-[11px] text-red-200/80">
                  {(preview.unmatched || []).map((u, i) => (
                    <li key={i}>row {u._csv_row}: {u.player_name} · team {u.team || "—"} · ({u.reason})</li>
                  ))}
                </ul>
              </details>
            )}
            {preview.conflicts && preview.conflicts.length > 0 && (
              <details open>
                <summary className="text-xs text-amber-300 cursor-pointer">Conflicts ({preview.conflicts.length})</summary>
                <ul className="mt-1 space-y-0.5 text-[11px] text-amber-200/80">
                  {preview.conflicts.map((c, i) => (
                    <li key={i}>row {c.csv_row}: {c.player_name} → #{c.jersey_number} in {c.age_group} (held by {c.currently_held_by})</li>
                  ))}
                </ul>
              </details>
            )}
            {preview.outcomes && preview.outcomes.length > 0 && (
              <details>
                <summary className="text-xs text-emerald-300 cursor-pointer">Per-row outcome ({preview.outcomes.length})</summary>
                <ul className="mt-1 space-y-0.5 text-[11px] text-slate-300">
                  {preview.outcomes.map((o, i) => (
                    <li key={i}>{o.player_name} · #{o.jersey_number_attempted ?? "—"} → {o.jersey_number_status} · size {o.jersey_size_attempted ?? "—"} → {o.jersey_size_status}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
